import Image from '@/components/Image';
import Dialog from '@/components/Dialog';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { accDiv, afterpayEssentialMap, floorNumber, klarnaEssentialMap } from '@/utils/tools';
import { InfoFilled, InfoOutlined, QuestionFilled } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import React, { useContext, useMemo, useState } from 'react';
import CycleTip from './CycleTip';
import { useKlarnaPaymentPromotion } from '@/components/Pay/KlarnaPromotion/useKlarnaPaymentPromotion';
import { IS_UK, IS_US } from '@/utils/constants';

interface CycleMsgProps {
  price: string | number;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
  variant?: 'default' | 'new'; // 新样式标识
}

const CycleMsg: React.FC<CycleMsgProps> = ({
  price,
  SDProductDetailsPageBtnClick,
  variant = 'default',
}) => {
  const currency = useStore((state) => state.currency);
  const [cycleTipShow, setCycleTipShow] = useState(false);
  const [showKlarnaTextDialog, setShowKlarnaTextDialog] = useState(false);
  const waiMode = useStore((state) => state.waiMode);
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const { isShowKlarnaPromotion, text_1, klarna_text, renderTextWithKlarnaLogo } =
    useKlarnaPaymentPromotion();

  const t = useTranslations('CycleMsg');
  const t2 = useTranslations('goodsDetail.CycleMsg');

  // 是否支持afterpay分期
  const afterpayEnable = useMemo(() => {
    return Object.keys(afterpayEssentialMap).includes(currency.name) && Number(price) > 10;
  }, [currency.name, price]);

  // afterpay分期价格
  const afterpayPrice = useMemo(() => {
    return floorNumber(accDiv(Number(price), 4));
  }, [price]);

  // klarna分期期数，0表示不支持
  const klarnaCycle = useMemo(() => {
    const info = Object.values(klarnaEssentialMap).find(
      (item) =>
        item.CURRENCY === currency.name &&
        item.CYCLE > 0 &&
        Number(price) >= item.MIN &&
        Number(price) <= item.MAX,
    );
    return info ? info.CYCLE : 0;
  }, [currency.name, price]);

  // klarna分期价格
  const klarnaPrice = useMemo(() => {
    if (klarnaCycle) {
      return floorNumber(accDiv(Number(price), klarnaCycle));
    }
    return '';
  }, [price, klarnaCycle]);

  // 处理提示图标点击
  const handleTipClick = () => {
    SDProductDetailsPageBtnClick?.('Klarna');
    setCycleTipShow(true);
  };

  // 如果既不支持afterpay也不支持klarna，则不显示组件
  if (!afterpayEnable && !klarnaCycle) {
    return null;
  }

  // 根据variant决定使用哪种样式
  const isNewStyle = variant === 'new';

  // 新样式文案：根据支持的支付方式生成文案
  const getNewStyleText = () => {
    const paymentMethods = [];
    if (afterpayEnable) paymentMethods.push(IS_UK ? 'Clearpay' : 'Afterpay');
    if (klarnaCycle) paymentMethods.push('klarna');

    if (paymentMethods.length === 0) return '';
    return t2('interestFreeInstallmentsOfNew', { paymentMethods: paymentMethods.join(', ') });
  };

  return (
    <div className="cycle-msg">
      {isNewStyle ? (
        <div className="flex items-center">
          <span
            className={`text-[12px] leading-[15px] ${waiMode ? 'text-primary-black' : 'text-[#808080]'}`}
          >
            {getNewStyleText()}
          </span>
          <InfoOutlined
            className="text-[13px] text-[#808080] align-middle ml-[4px] cursor-pointer"
            onClick={handleTipClick}
          />
        </div>
      ) : (
        <div className={`item-staging flex items-center py-4 ${isWeb ? '' : 'flex-wrap w-[104%]'}`}>
          <span
            className={`text-[12px] leading-[15px] ${waiMode ? 'text-primary-black' : 'text-[#808080]'}`}
          >
            {t('cycleInstalments', {
              cycle: afterpayEnable ? '4' : klarnaCycle,
              amount: formatPriceNumberWithSymbol(
                afterpayEnable ? afterpayPrice : klarnaPrice,
                currency,
              ),
            })}
          </span>
          {klarnaCycle ? (
            <Image
              src="/goods-detail/klarna.png"
              alt="klarna payment"
              width={50}
              height={12}
              style={{
                height: 'max(.8333333333vw, 12px)',
              }}
              className="w-auto align-middle ml-1"
            />
          ) : null}
          {afterpayEnable && klarnaCycle ? (
            <span className={`text-[11px] ${waiMode ? 'text-primary-black' : 'text-[#808080]'}`}>
              &nbsp;{t('or')}&nbsp;
            </span>
          ) : null}
          {afterpayEnable ? (
            <Image
              src={
                IS_UK
                  ? '/goods-detail/clearpay.png'
                  : IS_US
                    ? '/goods-detail/afterpay-us-v2.png'
                    : '/goods-detail/afterpay.png'
              }
              alt="afterpay payment"
              width={50}
              height={12}
              style={{
                height: 'max(.8333333333vw, 12px)',
              }}
              className="w-auto align-middle ml-1"
            />
          ) : null}

          <InfoFilled
            className="text-[13px] text-[#808080] align-middle ml-[.1041666667vw] cursor-pointer"
            onClick={handleTipClick}
          />
        </div>
      )}

      <CycleTip
        show={cycleTipShow}
        onClose={() => setCycleTipShow(false)}
        afterpayEnable={afterpayEnable}
        klarnaCycle={klarnaCycle}
      />
      {isShowKlarnaPromotion && (
        <div className="flex items-center text-[12px] leading-[15px] text-[#808080]">
          <span>
            {renderTextWithKlarnaLogo(
              text_1,
              <Image
                src="/goods-detail/klarna.png"
                alt="klarna payment"
                width={50}
                height={12}
                style={{
                  height: 'max(.8333333333vw, 12px)',
                }}
                className="w-auto align-middle ml-1"
              />,
            )}
          </span>
          {klarna_text && (
            <QuestionFilled
              onClick={() => setShowKlarnaTextDialog(true)}
              className="ml-[6px] text-[13px] text-black opacity-[0.5] cursor-pointer"
            />
          )}
        </div>
      )}
      {klarna_text &&
        (isWeb ? (
          <Dialog.Info
            open={showKlarnaTextDialog}
            title="Klarna"
            onClose={() => setShowKlarnaTextDialog(false)}
          >
            <div className="text-[12px] leading-[22px] text-[#808080] whitespace-pre-line break-words">
              {klarna_text}
            </div>
          </Dialog.Info>
        ) : (
          <Popup
            open={showKlarnaTextDialog}
            title="Klarna"
            onClose={() => setShowKlarnaTextDialog(false)}
          >
            <div className="text-[14px] leading-[22px] text-[#808080] whitespace-pre-line break-words">
              {klarna_text}
            </div>
          </Popup>
        ))}
    </div>
  );
};

export default CycleMsg;
